/* dataReformatter
 *
 * Handles formatting data from React Table to a HANA/Cayenne friendly format.
 * Generates the columns and data in the format needed by React Table.
 *
 * Used by:
 * - StatsTable.tsx
 */
import React from 'react';

import { DateTime } from 'luxon';
import { DefaultTheme } from 'styled-components';

import { hockeyFormatter } from '@nhl/formatter-utilities';
import { ReactTableEnums, ReactTableTypes } from '@nhl/nhl-breakaway-ui';

import { statsConfigSelectors } from '~/redux/ducks/config';
import { commonMessages, reportSpecificMessages } from '~/utility/commonMessages';
import { PLAYOFFS_ID } from '~/utility/constants';

import cellFormatter, { columnHelper } from './cellFormatter';
import { StatsRecord } from './types';

/* ===== Column + Data Generation ===== */
/* generateColumns
 *
 * There are multiple reports, each with their own measures to display. This function takes a report,
 * gets the necessary measures from that report, and transforms them into a ReactTable-friendly format.
 * Lastly, an extra column is added to display the row number.
 */
export const generateColumnsForMeasures = (
    statsConfig,
    appliedReport,
    reportCategory,
    intl,
    datasetColumns,
    theme: DefaultTheme,
    startingRowIndex = 0,
) => {
    // Determine column properties for use with React Table
    const columns = statsConfigSelectors.getMeasuresForReport(statsConfig, appliedReport, datasetColumns);

    // Calling it reactTableColumns here since it's formatted specifically for the table
    const reactTableColumns = columns.map((measure) => {
        let tooltip = '';
        if (measure === 'shootsCatches') {
            const shootsCatches = reportCategory === 'skater' ? 'playerShoots' : 'goalieCatches';
            tooltip = intl.formatMessage(commonMessages.tooltips[shootsCatches]);
        }

        // We check if we have a report specific message, otherwise use the general message
        const generalColumn = commonMessages.columns[measure];
        const reportSpecificColumn =
            reportSpecificMessages.columns[reportCategory]?.[appliedReport.reportName]?.[measure];
        const columnMessage = reportSpecificColumn || generalColumn;
        const reportMeasure = {
            label: columnMessage && intl.formatMessage(columnMessage, { br: <br /> }),
            measure,
            tooltip,
        };
        return cellFormatter(intl, reportMeasure, reportCategory, appliedReport.reportName, theme);
    });

    /* Edge case: Usually the opponent team abbreviation is hidden. However the days-between-games report should show
     * this. For the days-between-games report this column is only returned when the data is aggregated by game. */
    if (appliedReport.reportName === 'daysbetweengames') {
        for (let i = 0; i < reactTableColumns.length; i += 1) {
            if (reactTableColumns[i].accessorKey === 'opponentTeamAbbrev') {
                reactTableColumns[i].meta.hidden = false;
                reactTableColumns[i].minSize = 55;
            }
        }
    }

    // Edge case for goalie and team reports to exclude ot losses when game type is for playoffs
    if ((reportCategory === 'goalie' || reportCategory === 'team') && appliedReport.gameTypeId === PLAYOFFS_ID) {
        let { seasonFrom, seasonTo } = appliedReport;
        if (appliedReport.timeFrame === 'game' && !appliedReport.isDateRangeFromSeasons) {
            seasonFrom = hockeyFormatter.getApproximateSeasonByDate(DateTime.fromISO(appliedReport.dateFrom));
            seasonTo = hockeyFormatter.getApproximateSeasonByDate(DateTime.fromISO(appliedReport.dateTo));
        }
        /**
         * Our getApproximateSeasonByDate assumes that August is the start of a new season. However, in the 2019-20
         * season the playoffs started in August, and since statistical OT losses are included in the 2019-20 season this
         * column should be shown when that season is in range
         */
        const showOtLosses = 20202021 >= seasonFrom && 20192020 <= seasonTo;

        for (let i = 0; i < reactTableColumns.length; i += 1) {
            if (reactTableColumns[i].accessorKey === 'otLosses') {
                reactTableColumns[i].meta.hidden = !showOtLosses;
            }
        }
    }

    return [
        columnHelper.display({
            id: 'index',
            header: '',
            // ReactTable does not have an index column by default so here we use MATH to make it happen
            cell: (state: ReactTableTypes.CellContextType<StatsRecord, any>) =>
                startingRowIndex + (state?.rowIndex || 0),
            size: 46,
            maxSize: 60,
            meta: {
                excelExclude: true,
                pinned: ReactTableEnums.Pinned.LEFT,
            },
        }),
        ...reactTableColumns,
    ];
};

export default generateColumnsForMeasures;
